import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Link,
  CircularProgress,
  Alert,
  Fade,
  Slide,
  Zoom,
  Grow,
  Box
} from '@mui/material';
import { 
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { loginUser } from '../../services/auth';
import { keyframes } from '@emotion/react';
import Illustration from '../img/login-illustration.jpg'

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const data = await loginUser(email, password);
      if (data.status === 'success' && data.data.token) {
        localStorage.setItem('token', data.data.token);
        login(data.data.user);
        navigate(data.data.user.role === 'supervisor' ? '/dashboard' : '/tasks');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      py: 8
    }}>
      <Grow in={true} timeout={800}>
        <Grid container spacing={6} sx={{ alignItems: 'center' }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={6} sx={{
              p: 6,
              borderRadius: 4,
              background: 'linear-gradient(-45deg, #f3f4f6, #ffffff, #f8f9fa)',
              backgroundSize: '400% 400%',
              animation: `${gradientAnimation} 10s ease infinite`,
              position: 'relative',
              overflow: 'hidden',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                background: 'linear-gradient(45deg, #2196f3, #e91e63)',
                borderRadius: '50%',
                filter: 'blur(80px)',
                opacity: 0.15,
              }
            }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Slide in direction="down" timeout={500}>
                  <Typography variant="h3" component="h1" gutterBottom sx={{ 
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #2196f3 30%, #e91e63 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Welcome Back
                  </Typography>
                </Slide>

                <Fade in timeout={1000}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Please enter your credentials to login
                  </Typography>
                </Fade>
              </Box>

              {error && (
                <Slide in direction="right" timeout={300}>
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                    {error}
                  </Alert>
                </Slide>
              )}

              <form onSubmit={handleLogin}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {[
                    { 
                      field: 'email',
                      label: 'Email Address',
                      icon: <EmailIcon />,
                      type: 'email'
                    },
                    { 
                      field: 'password',
                      label: 'Password',
                      icon: <LockIcon />,
                      type: 'password',
                      extra: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  ].map((item, index) => (
                    <Grow 
                      key={item.field}
                      in={true}
                      timeout={index * 200 + 500}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      <TextField
                        fullWidth
                        required
                        type={item.type === 'password' ? (showPassword ? 'text' : 'password') : item.type}
                        label={item.label}
                        value={item.field === 'email' ? email : password}
                        onChange={(e) => item.field === 'email' ? 
                          setEmail(e.target.value) : setPassword(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {item.icon}
                            </InputAdornment>
                          ),
                          endAdornment: item.extra,
                          sx: { borderRadius: 3 }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            transition: 'all 0.3s',
                            '&.Mui-focused': {
                              boxShadow: '0 0 0 2px rgba(33,150,243,0.2)'
                            }
                          }
                        }}
                      />
                    </Grow>
                  ))}

                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Link href="/forgot-password" variant="body2" sx={{
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}>
                      
                    </Link>
                  </Box>

                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontSize: 16,
                      background: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : 'Login'}
                  </Button>

                  <Typography variant="body2" sx={{ textAlign: 'center' }}>
                    Don't have an account?{' '}
                    <Link href="/register" sx={{ fontWeight: 600 }}>
                      Sign up
                    </Link>
                  </Typography>
                </Box>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Zoom in={true} style={{ transitionDelay: '300ms' }}>
              <Box
                component="img"
                src={Illustration}
                alt="Login Illustration"
                sx={{ 
                  width: '100%',
                  height: 'auto',
                  maxWidth: 600,
                  ml: 'auto'
                }}
              />
            </Zoom>
          </Grid>
        </Grid>
      </Grow>
    </Container>
  );
};

export default Login;